import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Stack, Alert, IconButton, Typography, alpha, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { orderBy } from 'lodash';
import { useStores } from '../stores';
import { DeviceCheckResult } from 'components/MediaDevicesCheck';
import LoadingScreen from 'components/LoadingScreen';
import MediaDevicesCheck from 'components/MediaDevicesCheck';
import HelpDrawer from 'components/Interview/HelpDrawer';
import InterviewIntroModal from 'components/Interview/InterviewIntroModal';
import { personalDataStore } from 'stores/personalDataStore';
import { skillsStore } from 'stores/skillsStore';
import { useMediaDevices } from '../hooks/useMediaDevices';
import FeedbackDialog from 'components/FeedbackDialog';
import SpecialityModal from 'components/Profile/SpecialityModal';
import { userApi } from 'api';

type PermState = PermissionState | 'unsupported';

/**
 * Возвращает состояние разрешений на камеру/микрофон через Permissions API.
 * Не все браузеры поддерживают query по 'camera'/'microphone' (старые Firefox/Safari) —
 * в этом случае возвращаем 'unsupported', и вызывающий код просто пробует getUserMedia.
 */
const getPermissionStates = async (): Promise<{ camera: PermState; microphone: PermState }> => {
  const query = async (name: 'camera' | 'microphone'): Promise<PermState> => {
    try {
      if (!navigator.permissions?.query) return 'unsupported';
      const status = await navigator.permissions.query({ name: name as PermissionName });
      return status.state;
    } catch {
      return 'unsupported';
    }
  };
  const [camera, microphone] = await Promise.all([query('camera'), query('microphone')]);
  return { camera, microphone };
};

const DENIED_PERMISSION_MESSAGE =
  'Доступ к камере или микрофону заблокирован в браузере. Нажмите на иконку в адресной строке, ' +
  'разрешите доступ к камере и микрофону.';

/** Определяем браузер, чтобы показать подсказку только там, где она есть (пока только Chrome). */
const isChromeBrowser = (): boolean => {
  const ua = navigator.userAgent;
  return /chrome|crios/i.test(ua) && !/edg|opr|opera|samsungbrowser/i.test(ua);
};

/** Скриншот-подсказка, где находится иконка разрешений в адресной строке Chrome. */
const CHROME_PERMISSION_HINT_IMAGE = '/static/permissions/chrome.png';

/** Ключ localStorage: показывали ли уже вступительное окно про видео-интервью. */
const INTRO_SHOWN_STORAGE_KEY = 'interviewIntroIsShowed';

/** Показывали ли уже вступительное окно (читаем из localStorage, безопасно к ошибкам). */
const isIntroAlreadyShowed = (): boolean => {
  try {
    return localStorage.getItem(INTRO_SHOWN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/** Запоминаем, что вступительное окно уже показали. */
const markIntroShowed = (): void => {
  try {
    localStorage.setItem(INTRO_SHOWN_STORAGE_KEY, 'true');
  } catch {
    // localStorage может быть недоступен (приватный режим и т.п.) — игнорируем
  }
};

const validateDevices = (
  result: DeviceCheckResult | null
): { isValid: boolean; errorMessage: string } => {
  if (!result) {
    return { isValid: false, errorMessage: 'Устройства не проверены' };
  }
  if (!result.microphone.hasPermission || result.microphone.devices.length === 0) {
    return {
      isValid: false,
      errorMessage:
        'Микрофон недоступен. Пожалуйста, подключите микрофон и предоставьте разрешение на его использование.'
    };
  }
  if (!result.camera.hasPermission || result.camera.devices.length === 0) {
    return {
      isValid: false,
      errorMessage:
        'Камера недоступна. Пожалуйста, подключите камеру и предоставьте разрешение на её использование.'
    };
  }
  return { isValid: true, errorMessage: '' };
};

const CheckDevicesPage = observer(() => {
  const navigate = useNavigate();
  const { id: requestedInterviewId } = useParams();
  const isRequested = Boolean(requestedInterviewId);

  const { interviewStore } = useStores();
  // autoStart: false — не запрашиваем доступ к камере/микрофону при заходе на страницу.
  // Сначала показываем вступительное окно, и только после его закрытия — запрашиваем доступ.
  const { checkDevices, devicesResult } = useMediaDevices({ autoStart: false });

  const [error, setError] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const { userLoading } = personalDataStore;
  const isLoading = interviewStore.loading;
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [specialityModalOpen, setSpecialityModalOpen] = useState(false);
  const [proceedAfterSave, setProceedAfterSave] = useState(false);
  // Вступительное окно: показывается только при первом заходе (флаг в localStorage),
  // объясняет процесс и зачем нужен доступ. Доступ к камере/микрофону запрашивается
  // только после его закрытия. Если окно уже показывали — сразу запускаем тот же поток.
  const [introOpen, setIntroOpen] = useState(() => !isIntroAlreadyShowed());
  // Обработали ли шаг после закрытия intro (запрос доступа / открытие модалки специализации) — один раз.
  const postIntroHandledRef = useRef(false);
  // Нужно ли после сохранения сразу продолжать к тесту. true — только когда
  // модалку открыли по клику на кнопку; при авто-открытии остаёмся на странице.
  const proceedIntentRef = useRef(false);

  const theme = useTheme();

  // Картинка-подсказка под сообщением о заблокированных разрешениях — пока только для Chrome.
  const permissionHintImage = useMemo(
    () => (isChromeBrowser() ? CHROME_PERMISSION_HINT_IMAGE : null),
    []
  );

  const setIsLoading = useCallback(
    (loading: boolean) => {
      interviewStore.loading = loading;
    },
    [interviewStore]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const tasks: Promise<unknown>[] = [personalDataStore.getExtractInterviews()];
        if (isRequested) {
          tasks.push(personalDataStore.getRequestedInterviews());
        }
        await Promise.all(tasks);
      } catch {
        setError('Произошла ошибка');
      } finally {
        setIsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsLoading, isRequested]);

  const requestedInterview = useMemo(() => {
    if (!isRequested) return null;
    return personalDataStore.requestedInterviews.find((i) => i.id === requestedInterviewId) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequested, requestedInterviewId, personalDataStore.requestedInterviews]);

  const extractInterview = useMemo(() => {
    const all = personalDataStore.extractInterviews;

    if (isRequested) {
      if (!requestedInterview?.vacancyId) return null;
      return all.find((i) => i.vacancyId === requestedInterview.vacancyId) ?? null;
    }

    const user = personalDataStore.user;
    if (!user) return null;
    const level = user.specialityLevel?.toLowerCase();
    const candidates = all.filter(
      (i) =>
        !i.vacancyId &&
        i.speciality === user.speciality &&
        i.level === level &&
        i.name === user.position
    );
    return orderBy(candidates, ['createdAt'], ['asc'])[0] ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRequested,
    requestedInterview?.vacancyId,
    personalDataStore.extractInterviews,
    personalDataStore.user
  ]);

  // Лениво запрашивает доступ к камере/микрофону (если ещё не выдан) и проверяет устройства.
  // Возвращает true, если всё ок. Сообщение об ошибке (с инструкцией при блокировке) ставит само.
  const requestAndValidateDevices = async (): Promise<boolean> => {
    let result: DeviceCheckResult | null = devicesResult;
    if (!validateDevices(result).isValid) {
      // Нативный диалог браузера появится только здесь — по действию пользователя.
      result = (await checkDevices()) ?? null;
    }
    const validation = validateDevices(result);
    if (validation.isValid) {
      setError(null);
      return true;
    }
    // Если доступ именно заблокирован — показываем инструкцию (в Chrome ещё и картинку).
    const { camera, microphone } = await getPermissionStates();
    setError(
      camera === 'denied' || microphone === 'denied'
        ? DENIED_PERMISSION_MESSAGE
        : validation.errorMessage
    );
    return false;
  };

  const handleProceedToTest = async () => {
    setError(null);

    // Обычный поток: сначала убеждаемся, что заполнены специализация/должность/уровень —
    // модалку показываем ДО запроса доступа к камере/микрофону. Разрешения запрашиваются
    // после её сохранения (onSubmit) и здесь, по кнопке. В requested-потоке данные берутся
    // из вакансии, поэтому модалка не нужна.
    if (!isRequested) {
      const u = personalDataStore.user;
      if (!u) {
        if (!userLoading) {
          setError('Не удалось загрузить пользователя');
        }
        return;
      }
      if (!u.position || !u.speciality || !u.specialityLevel) {
        proceedIntentRef.current = true;
        setSpecialityModalOpen(true);
        return;
      }
    }

    // Лениво запрашиваем доступ к устройствам и проверяем результат.
    const devicesOk = await requestAndValidateDevices();
    if (!devicesOk) return;

    setIsLoading(true);
    // let fetchExtractResultsImmediately =  false;
    try {
      if (isRequested) {
        if (!requestedInterview) {
          setError('Не удалось получить запрошенное интервью');
          return;
        }

        await skillsStore.getSkillsWithGrade();

        if (!extractInterview) {
          await skillsStore.extractCompetences(
            {
              position: requestedInterview.vacancy.position,
              speciality: requestedInterview.vacancy.speciality,
              level: requestedInterview.vacancy.specialityLevel
            },
            requestedInterview.vacancyId
          );
        } else if (extractInterview.finish) {
          await interviewStore.startInterview(
            extractInterview.interviewId as string,
            requestedInterview.vacancyId
          );
          if (interviewStore.currentInterview?.id) {
            navigate(
              `/requested-interviews/video-interview/record/${interviewStore.currentInterview.id}`
            );
          } else {
            setError('Не удалось создать интервью');
          }
          return;
        } else if (extractInterview.results?.summary || extractInterview.index >= 2) {
          navigate(`/skill-lvl/${extractInterview.interviewId}`);
          return;
        } else {
          skillsStore.questions = extractInterview.questions;
          skillsStore.interviewId = extractInterview.interviewId;
          skillsStore.authInterviewId = extractInterview.id;
          skillsStore.questionIndex = extractInterview.index || 0;
          // fetchExtractResultsImmediately = extractInterview.index >= 2
        }
        // skillsStore.fetchExtractResultsImmediately = fetchExtractResultsImmediately
        navigate(`/extract-skills/${skillsStore.interviewId}`);
        return;
      }

      const user = personalDataStore.user;
      if (!user) {
        if (!userLoading) {
          setError('Не удалось загрузить пользователя');
        }
        return;
      }

      await skillsStore.getSkillsWithGrade();

      if (!extractInterview) {
        await skillsStore.extractCompetences({
          position: user.position,
          speciality: user.speciality,
          level: user.specialityLevel
        });
      } else if (extractInterview.finish) {
        await interviewStore.startInterview(extractInterview.interviewId as string);
        if (interviewStore.currentInterview?.id) {
          navigate(`/video-interview/record/${interviewStore.currentInterview.id}`);
        } else {
          setError('Не удалось создать интервью');
        }
        return;
      } else if (extractInterview.results?.summary) {
        navigate(`/skill-lvl/${extractInterview.interviewId}`);
        return;
      } else {
        skillsStore.questions = extractInterview.questions;
        skillsStore.interviewId = extractInterview.interviewId;
        skillsStore.authInterviewId = extractInterview.id;
        // fetchExtractResultsImmediately = extractInterview.index >= 2
        skillsStore.questionIndex = extractInterview.index || 0;
      }
      // skillsStore.fetchExtractResultsImmediately = fetchExtractResultsImmediately
      navigate(`/extract-skills/${skillsStore.interviewId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  // После сохранения данных в модалке user обновляется через refreshUser(),
  // компонент перерендеривается с актуальными speciality/position/level —
  // и мы автоматически продолжаем переход к проверке навыков.
  useEffect(() => {
    if (!proceedAfterSave) return;
    const user = personalDataStore.user;
    if (user?.position && user?.speciality && user?.specialityLevel) {
      setProceedAfterSave(false);
      handleProceedToTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proceedAfterSave, personalDataStore.user]);

  // Закрытие вступительного окна — запоминаем в localStorage, чтобы больше не показывать.
  // Сам запрос доступа выполняет эффект ниже.
  const handleIntroConfirm = () => {
    markIntroShowed();
    setIntroOpen(false);
  };

  // После закрытия вступительного окна (один раз за визит):
  //  • если не заполнены специализация/должность/уровень — открываем модалку с ними,
  //    доступ к устройствам запросится после её сохранения (см. onSubmit);
  //  • иначе сразу запрашиваем доступ к камере/микрофону.
  // В requested-потоке данные берутся из вакансии, поэтому модалка специализации не нужна.
  useEffect(() => {
    if (introOpen) return;
    if (postIntroHandledRef.current) return;

    if (isRequested) {
      postIntroHandledRef.current = true;
      requestAndValidateDevices();
      return;
    }

    if (userLoading) return;
    const user = personalDataStore.user;
    if (!user) return;

    postIntroHandledRef.current = true;
    if (!user.position || !user.speciality || !user.specialityLevel) {
      proceedIntentRef.current = false;
      setSpecialityModalOpen(true);
    } else {
      requestAndValidateDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introOpen, isRequested, userLoading, personalDataStore.user]);

  const handleRetryDevices = async () => {
    setError(null);

    // Сначала смотрим реальное состояние разрешений. Если хоть одно из них
    // 'denied' — браузер НЕ покажет повторный диалог по getUserMedia, поэтому
    // нет смысла его дёргать: показываем инструкцию, как разблокировать вручную.
    const { camera, microphone } = await getPermissionStates();
    if (camera === 'denied' || microphone === 'denied') {
      setError(DENIED_PERMISSION_MESSAGE);
      return;
    }

    // Состояние 'prompt' (или браузер не поддерживает Permissions API) —
    // вызов getUserMedia реально покажет нативный диалог запроса разрешений.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      // Пользователь только что нажал «Запретить» — getUserMedia вернёт NotAllowedError.
      if ((err as DOMException)?.name === 'NotAllowedError') {
        setError(DENIED_PERMISSION_MESSAGE);
        return;
      }
      // прочие ошибки (устройство занято и т.п.) разберёт checkDevices/validateDevices ниже
    }

    const fresh = await checkDevices();
    const validation = validateDevices(fresh ?? devicesResult);
    if (!validation.isValid) {
      setError(validation.errorMessage);
    }
  };

  // Автоматически перепроверяем устройства, когда пользователь вручную
  // меняет разрешение через адресную строку браузера (без перезагрузки страницы).
  useEffect(() => {
    if (!navigator.permissions?.query) return;
    let cancelled = false;
    const statuses: PermissionStatus[] = [];

    const handleChange = async () => {
      if (cancelled) return;
      const fresh = await checkDevices();
      const validation = validateDevices(fresh ?? devicesResult);
      setError(validation.isValid ? null : validation.errorMessage);
    };

    (['camera', 'microphone'] as const).forEach(async (name) => {
      try {
        const status = await navigator.permissions.query({ name: name as PermissionName });
        if (cancelled) return;
        status.addEventListener('change', handleChange);
        statuses.push(status);
      } catch {
        // браузер не поддерживает query по этому имени — пропускаем
      }
    });

    return () => {
      cancelled = true;
      statuses.forEach((s) => s.removeEventListener('change', handleChange));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDevices]);

  if (isLoading) {
    return <LoadingScreen label="Подготавливаем вопросы..." />;
  }

  // Оплата теперь запрашивается на странице результатов интервью, а не перед ним.
  const buttonLabel = extractInterview?.finish
    ? 'Перейти к интервью'
    : 'Перейти к проверке навыков';

  return (
    <Stack spacing={4} sx={{ height: '100%', overflow: 'auto', ml: -3, mr: -3, pl: 3, pr: 3 }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton color="secondary" onClick={handleRetryDevices}>
              <Icon icon="mdi:refresh" />
            </IconButton>
          }
        >
          {error}
          {error === DENIED_PERMISSION_MESSAGE && permissionHintImage && (
            <Box
              component="img"
              src={permissionHintImage}
              alt="Где найти иконку разрешений в адресной строке браузера"
              sx={{
                display: 'block',
                mt: 1.5,
                maxWidth: '100%',
                width: 360,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
              }}
            />
          )}
        </Alert>
      )}
      <Box sx={{ mt: 3 }}>
        <MediaDevicesCheck devicesResult={devicesResult} />
        <Box sx={{ mt: 4 }}>
          <Typography
            textAlign="center"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, mb: 4 }}
          >
            <Button
              variant="text"
              sx={{ p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
              onClick={() => setIsHelpOpen(true)}
              color='secondary'
            >
              Посмотреть подробную инструкцию
            </Button>{' '}
          </Typography>
        </Box>
      </Box>
      <InterviewIntroModal open={introOpen} onConfirm={handleIntroConfirm} />
      <HelpDrawer open={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <SpecialityModal
        open={specialityModalOpen}
        onClose={() => setSpecialityModalOpen(false)}
        onSubmit={async (values) => {
          if (!personalDataStore.userId) return;
          await userApi.updateUser(personalDataStore.userId, {
            speciality: values.speciality,
            position: values.position,
            specialityLevel: values.specialityLevel
          });
          await personalDataStore.refreshUser();
          // Данные заполнены — теперь запрашиваем доступ к камере/микрофону
          // (нативный диалог браузера). Делаем это по событию сохранения модалки.
          await requestAndValidateDevices();
        }}
        onSaved={() => {
          if (proceedIntentRef.current) {
            setProceedAfterSave(true);
          }
          proceedIntentRef.current = false;
        }}
      />
      <Stack direction="column" alignItems="center" justifyContent="center" spacing={2} sx={{ mt: 'auto !important' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleProceedToTest}
          disabled={isLoading}
          size="large"
          sx={{ maxWidth: 800 }}
          startIcon={isLoading ? <Icon icon="mdi:loading" /> : undefined}
        >
          {buttonLabel}
        </Button>
      </Stack>
      <Stack justifyContent="center" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<Icon icon="mdi:headset" width={18} height={18} />}
          color="info"
          onClick={() => setFeedbackOpen(true)}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 700,
            borderColor: 'grey.300',
            color: 'text.primary',
            width: '250px',
            mb: 4,
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.04)
            }
          }}
        >
          Написать поддержке
        </Button>
      </Stack>
    </Stack>
  );
});

export default CheckDevicesPage;
